import { Button, Chip, Stack, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { format } from "date-fns";
import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useGetInvoiceByIdQuery } from "src/api/services/invoicesApi";
import { Order } from "src/api/types";
import { stripeStatusMap } from "src/utils/stripeStatusMap";

interface Props {
  order: Order;
}

const InvoiceInformation = ({ order }: Props) => {
  const { data: invoice } = useGetInvoiceByIdQuery(
    order.invoice_id ? order.invoice_id : skipToken,
  );

  const amount = useMemo(() => {
    if (invoice == null) return null;

    return invoice.amount.toFixed(2);
  }, [invoice]);

  const dateFormatter = useCallback((timestamp: string | number | null) => {
    if (timestamp == null) return "N/A";

    let date: Date | null = null;

    if (typeof timestamp === "number") {
      date = new Date(timestamp * 1000);
    } else {
      date = new Date(timestamp);
    }

    return format(date, "MM/dd/yyyy");
  }, []);

  const stripeUrl = useMemo(
    () =>
      import.meta.env.PROD
        ? `https://dashboard.stripe.com/invoices/${order.invoice_id}`
        : `https://dashboard.stripe.com/test/invoices/${order.invoice_id}`,
    [order.invoice_id],
  );

  if (invoice == null) return null;

  return (
    <Stack alignItems={"end"} gap={1}>
      <Stack direction={"row"} gap={1}>
        {stripeStatusMap[invoice.status] == null ? null : (
          <Chip
            label={
              <Typography color="#ffffff">
                {stripeStatusMap[invoice.status]?.text}
              </Typography>
            }
            color={stripeStatusMap[invoice.status]?.color}
          />
        )}
        <Link to={stripeUrl} target="_blank">
          <Button variant="secondary">
            <Typography fontWeight={700} variant="body1">
              {"Invoice (Stripe)"}
            </Typography>
          </Button>
        </Link>
      </Stack>
      <Stack direction={"row"} spacing={5}>
        <Typography fontWeight={600}>Amount:</Typography>
        <Typography>${amount}</Typography>
      </Stack>
      {invoice.status === "paid" ? (
        <Stack direction={"row"} spacing={3}>
          <Typography fontWeight={600}>Charge date:</Typography>
          <Typography>{dateFormatter(invoice.paid_at)}</Typography>
        </Stack>
      ) : invoice.status === "open" ? (
        <Stack gap={1}>
          <Stack direction={"row"} spacing={5}>
            <Typography fontWeight={600}>Last attempt:</Typography>
            <Typography>{dateFormatter(order.last_attempt_at)}</Typography>
          </Stack>
          <Stack direction={"row"} spacing={5}>
            <Typography fontWeight={600}>Next attempt:</Typography>
            <Typography>{dateFormatter(order.next_attempt_at)}</Typography>
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
};

export default InvoiceInformation;
